import { Box, Grid, Stack } from "@mui/material";
import { isEmpty } from "lodash-es";
import { type FC, useMemo, useRef } from "react";
import { FormProvider, useFormContext, useWatch } from "react-hook-form";
import { useHandleMomentLexicalError } from "../generic/hooks/use-handle-moment-lexical-error";
import { useSetLexicalDesign } from "../generic/hooks/use-set-lexical-design";
import type { ToolbarSettings } from "../lexical-email-moment/hooks/use-email-toolbar-settings";
import { useFilterTags } from "../merge-tag/components/use-filter-tags";
import type { Tags } from "../merge-tag/types";
import { DefaultBodyPlugins } from "../message/editor/default-body-plugins";
import { DefaultContentEditable } from "../message/editor/default-content-editable";
import { DefaultPlaceholder } from "../message/editor/default-placeholder";
import { useLexicalContext } from "../message/editor/lexical-context";
import { LexicalDynamicContentContextProvider } from "../message/editor/lexical-dynamic-content-context";
import { LexicalEditorWrapper } from "../message/editor/lexical-editor-wrapper";
import { DynamicContentPlugin } from "../message/editor/plugins/dynamic-content-plugin/dynamic-content-plugin";
import { GenericToolbar } from "../message/editor/plugins/generic-toolbar/generic-toolbar";
import { ImagePromptPlugin } from "../message/editor/plugins/image-prompt-plugin";
import { PollPluginMomentFormWrapper } from "../message/editor/plugins/poll-plugin/poll-plugin-moment-form-wrapper";
import { SurveyLinkToTargetSwitchPlugin } from "../message/editor/plugins/survey-link-to-target-switch-plugin";
import { SurveyAnswersPlugin } from "../message/editor/plugins/survey-answers-plugin/survey-answers-plugin";
import type { SerialisedEditorDesign } from "../message/editor/types/serialised-editor-design";
import { chatToolbarElements } from "../message/editor/utils/toolbar-elements/chat-toolbar-elements";
import type { MomentFormMoment } from "../moment/types/moment-form-moment";
import { useToTarget } from "../to/use-to-target";
import { makeDefaultLexicalDesign } from "./make-default-lexical-design";
import { MomentSlackAliasSelector } from "./slack-alias-selector";
import { useMomentFormSurveys } from "../survey/hooks/use-moment-form-surveys";

interface ChatMomentFormProps {
  disabled?: boolean;
  showValidationErrors: boolean;
  mergeTags: Tags;
}

export const ChatMomentForm: FC<{
  disabled?: boolean;
  mergeTags: Tags;
  error?: string;
  channel: "slack" | "teams";
  design?: SerialisedEditorDesign;
}> = ({ disabled, mergeTags, error, channel, design }) => {
  const onChange = useSetLexicalDesign();
  const ref = useRef<HTMLDivElement>(null);
  const toTarget = useToTarget();
  const { control } = useFormContext<MomentFormMoment>();
  const triggerSurveyId = useWatch({ control, name: "trigger_survey_id" });
  const { data: surveys } = useMomentFormSurveys();
  const triggerSurveyName = surveys?.find((survey) => survey.id === triggerSurveyId)?.title ?? null;

  const toolbarSettings = useMemo(() => {
    const elements = chatToolbarElements(channel, toTarget.toTarget);

    const floating = {
      ...elements.floating,
      text: [
        ...(elements.floating.text ?? []),
        { type: "coPilot" as const, channel, format: "chat" as const },
      ],
    };

    return {
      channel,
      topLevelElements: elements.topLevel,
      floatingElements: floating,
    };
  }, [channel, toTarget]);

  const onError = useHandleMomentLexicalError();

  const filterTags = useFilterTags(
    mergeTags.filter((x) => x.version === 2),
    toTarget,
  );

  return (
    <Grid container>
      <Grid size={{ xs: 12 }}>
        <LexicalDynamicContentContextProvider>
          <LexicalEditorWrapper
            channelName={channel}
            toChannel={toTarget.toTarget === "channel"}
            disabled={disabled ?? false}
            onError={(...args) => void onError(...args).catch(console.error)}
            error={error}
            filterTags={filterTags}
            design={isEmpty(design) ? makeDefaultLexicalDesign("chat") : design}
            format="chat"
            isSurveyTrigger={!!triggerSurveyId}
            triggerSurveyId={triggerSurveyId}
            triggerSurveyName={triggerSurveyName}
            renderLayout={(editor) => <Box ref={ref}>{editor}</Box>}
            slots={{
              contentEditable: ({ focusEditor, onRef }) => (
                <DefaultContentEditable
                  focusEditor={focusEditor}
                  onRef={onRef}
                  channel={channel}
                  disabled={disabled}
                />
              ),
              placeholder: <DefaultPlaceholder />,
              plugins: {
                head: () => (
                  <ChatHeadPlugins toolbarSettings={toolbarSettings} disabled={disabled} />
                ),
                body: (historyState) => (
                  <>
                    <DefaultBodyPlugins
                      auto_focus={false}
                      channel={channel}
                      format="chat"
                      disabled={disabled}
                      onChange={onChange}
                      editorScrollRef={ref}
                      toolbarSettings={toolbarSettings}
                      historyState={historyState}
                      v1Tags={mergeTags.filter((x) => x.version === 1)}
                    />
                    <PollPluginMomentFormWrapper />
                    <SurveyAnswersPlugin />
                    <DynamicContentPlugin />
                    <ImagePromptPlugin disabled={!!disabled} />
                    <SurveyLinkToTargetSwitchPlugin toTarget={toTarget.toTarget} />
                  </>
                ),
              },
            }}
          />
        </LexicalDynamicContentContextProvider>
      </Grid>
    </Grid>
  );
};

const ChatHeadPlugins: FC<{
  toolbarSettings: ToolbarSettings;
  disabled?: boolean;
}> = ({ toolbarSettings, disabled }) => {
  // This will eventuall be rendered by the LexicalEditor calling the slot, so we can
  // use the context here
  const {
    editorModal: { handleOpen },
  } = useLexicalContext();

  return (
    <GenericToolbar
      channel={toolbarSettings.channel}
      topLevelElements={toolbarSettings.topLevelElements}
      disabled={!!disabled}
      openModal={handleOpen}
    />
  );
};

export const LexicalSlackMomentForm: FC<ChatMomentFormProps> = ({
  disabled,
  showValidationErrors,
  mergeTags,
}) => {
  const methods = useFormContext<MomentFormMoment>();
  const {
    formState: { touchedFields, errors },
  } = methods;

  return (
    <FormProvider {...methods}>
      <Stack direction="column" spacing={1}>
        <MomentSlackAliasSelector />

        <ChatMomentForm
          disabled={disabled}
          mergeTags={mergeTags}
          error={showValidationErrors || touchedFields?.design ? errors.design?.message : undefined}
          channel="slack"
          design={methods.getValues("design") ?? undefined}
        />
      </Stack>
    </FormProvider>
  );
};

export const LexicalTeamsMomentForm: FC<ChatMomentFormProps> = ({
  disabled,
  showValidationErrors,
  mergeTags,
}) => {
  const methods = useFormContext<MomentFormMoment>();
  const {
    formState: { touchedFields, errors },
  } = methods;

  return (
    <FormProvider {...methods}>
      <Grid container>
        <Grid size={{ xs: 12 }}>
          <ChatMomentForm
            disabled={disabled}
            mergeTags={mergeTags}
            error={
              showValidationErrors || touchedFields?.design ? errors.design?.message : undefined
            }
            channel="teams"
            design={methods.getValues("design") ?? undefined}
          />
        </Grid>
      </Grid>
    </FormProvider>
  );
};
